import { Ionicons } from '@expo/vector-icons';
import { Image } from 'expo-image';
import { useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { StyleSheet, TouchableOpacity, View } from 'react-native';
import { ContextMenuAction } from 'react-native-context-menu-view';
import Animated, { Easing, FadeIn, FadeOut, ReduceMotion, useAnimatedStyle, withTiming } from 'react-native-reanimated';

import {
  Chat,
  DeleteMessageData,
  EditMessageData,
  NewMessageData,
  ReplyMessageData
} from '@/api/models/chatRoomModels';
import { bs_icon } from '@/assets/images';
import { ChatActions } from '@/constants/enums/ChatActions';
import { smartFormatDate } from '@/utils/dateFormats';
import { i18n } from '@/utils/i18n';

import TextInputField from '../inputs/TextInputField';
import Text from '../Text';
import ContextMenu from './ContextMenu.web';
import BrandedButton from '../buttons/BrandedButton';
import { parseReplyContext } from '@/utils/chatUtils';

interface ChatMessageProps {
  roomId: string;
  chat: Chat;
  actions?: ContextMenuAction[];
  thumbnail?: string;
  sender: string;
  roomSubtitle?: string;
  onRespond?: (data: NewMessageData) => void;
  onEdit?: (data: EditMessageData) => void;
  onDelete?: (data: DeleteMessageData) => void;
}

const ChatMessage = ({
  roomId,
  chat,
  actions,
  thumbnail,
  sender,
  roomSubtitle,
  onRespond,
  onEdit,
  onDelete
}: ChatMessageProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isResponding, setIsResponding] = useState(false);

  const {
    setValue: setResponseValue,
    control: responseControl,
    handleSubmit: handleResponse
  } = useForm<ReplyMessageData>({
    mode: 'onBlur'
  });

  const {
    setValue: setEditValue,
    control: editControl,
    handleSubmit: handleEdit
  } = useForm<{ editMessage: string }>({
    mode: 'onBlur'
  });

  const onMsgResponded: SubmitHandler<ReplyMessageData> = async (data) => {
    if (onRespond) onRespond(data);
    setResponseValue('newMessage', '');
    setResponseValue('repliedMessage', '');
    setResponseValue('repliedMessageFrom', '');
    setIsResponding(false);
  };

  const onMsgEdited: SubmitHandler<EditMessageData> = async (data) => {
    if (onEdit) onEdit(data);
    setEditValue('editMessage', '');
    setIsEditing(false);
  };

  const onMsgDeleted = async (data: DeleteMessageData) => {
    if (onDelete) onDelete(data);
  };

  const hideRespondingStyle = useAnimatedStyle(() => ({
    opacity: withTiming(isResponding ? 0 : 1, {
      duration: 300,
      easing: Easing.inOut(Easing.quad),
      reduceMotion: ReduceMotion.System
    })
  }));

  // Remove respond action from context menu because of new "Direct Message" button
  const filteredActions = actions?.filter((it) => it.title !== ChatActions.RESPOND);

  const { reply: replyContext, text: msg } = parseReplyContext(chat.msg);

  return (
    <View style={styles.chatMessageContainer}>
      <ContextMenu
        disabled={isEditing || isResponding}
        actions={filteredActions}
        onPress={(e) => {
          switch (e) {
            case ChatActions.RESPOND:
              if (onRespond) {
                setIsResponding(true);
              }
              break;

            case ChatActions.EDIT:
              if (onEdit) {
                setIsEditing(true);
                setEditValue('editMessage', chat.msg);
              }
              break;

            case ChatActions.DELETE:
              if (onDelete) {
                onMsgDeleted({ idMessage: chat.id });
              }
              break;

            default:
              break;
          }
        }}>
        <Animated.View style={styles.mainContainer}>
          <View style={styles.innerContainer}>
            <Image source={thumbnail ? thumbnail : bs_icon} style={styles.thumbnail} />
            <View style={styles.textContainer}>
              {isEditing ? (
                <Animated.View entering={FadeIn} exiting={FadeOut} style={{ flex: 1 }}>
                  <TextInputField
                    name="editMessage"
                    control={editControl}
                    placeholder={i18n.t('messages.edit_message_placeholder')}
                    hideError
                    onSubmitEditing={handleEdit((data) =>
                      onMsgEdited({
                        roomId: roomId,
                        idMessage: chat.id ?? '',
                        message: data.editMessage
                      })
                    )}
                    variant="chat"
                    multiline
                    leadingIcon={
                      <TouchableOpacity
                        onPress={() => {
                          setIsEditing(false);
                        }}>
                        <Ionicons size={24} name="close" style={styles.grayColor} />
                      </TouchableOpacity>
                    }
                    traillingIcon={
                      <TouchableOpacity
                        onPress={handleEdit((data) =>
                          onMsgEdited({
                            roomId: roomId,
                            idMessage: chat.id ?? '',
                            message: data.editMessage
                          })
                        )}>
                        <Ionicons size={20} name="send" style={styles.grayColor} />
                      </TouchableOpacity>
                    }
                  />
                </Animated.View>
              ) : (
                <>
                  {replyContext && (
                    <View style={styles.replyContainer}>
                      <Text fontWeight="700" style={styles.replyTitle}>
                        {replyContext.repliedMessageFrom ?? '-'}
                      </Text>
                      <Text fontWeight="600" style={styles.replySubtitle}>
                        {replyContext.repliedMessageFromSubtitle ?? '-'}
                      </Text>
                      <Text>{replyContext.repliedMessage}</Text>
                    </View>
                  )}
                  <Animated.Text
                    entering={FadeIn}
                    exiting={FadeOut}
                    style={styles.blackText}
                    numberOfLines={1}
                    allowFontScaling={false}>
                    {sender}
                  </Animated.Text>
                  <Animated.Text entering={FadeIn} exiting={FadeOut} allowFontScaling={false}>
                    {msg}
                  </Animated.Text>
                </>
              )}
            </View>
          </View>
        </Animated.View>
        {chat.editAt && chat.editAt !== chat.createdAt && (
          <Animated.View style={[styles.editedLabel, hideRespondingStyle]}>
            <Text>{i18n.t('mix.edited')}</Text>
          </Animated.View>
        )}
        <Animated.View style={[styles.date, hideRespondingStyle]}>
          <Text numberOfLines={2}>{`${smartFormatDate(chat.createdAt ?? '')}`}</Text>
        </Animated.View>

        {actions?.find((it) => it.title === ChatActions.RESPOND) && (
          <Animated.View style={[styles.replyButton, hideRespondingStyle]}>
            <BrandedButton
              onPress={() => {
                if (onRespond) {
                  setIsResponding(true);
                }
              }}>
              <Text fontWeight="700" style={styles.directMessageLabel}>
                {i18n.t('mix.direct_message')}
              </Text>
            </BrandedButton>
          </Animated.View>
        )}
      </ContextMenu>

      {isResponding && (
        <TextInputField
          name="newMessage"
          control={responseControl}
          placeholder={i18n.t('messages.respond_message_placeholder')}
          hideError
          onSubmitEditing={handleResponse((data) =>
            onMsgResponded({
              newMessage: data.newMessage,
              repliedMessageFrom: sender ?? chat.sender ?? roomSubtitle ?? '',
              repliedMessageFromSubtitle: roomSubtitle,
              repliedMessage: chat.msg
            })
          )}
          leadingIcon={
            <TouchableOpacity
              onPress={() => {
                setIsResponding(false);
              }}>
              <Ionicons size={24} name="close" style={styles.grayColor} />
            </TouchableOpacity>
          }
          traillingIcon={
            <TouchableOpacity
              onPress={handleResponse((data) =>
                onMsgResponded({
                  newMessage: data.newMessage,
                  repliedMessageFrom: sender ?? chat.sender ?? roomSubtitle ?? '',
                  repliedMessageFromSubtitle: roomSubtitle,
                  repliedMessage: chat.msg
                })
              )}>
              <Ionicons size={20} name="send" style={styles.grayColor} />
            </TouchableOpacity>
          }
          variant="rounded_with_shadow"
        />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  mainContainer: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'space-between'
  },

  innerContainer: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'flex-start',
    paddingEnd: 8,
    gap: 10
  },

  thumbnail: {
    width: 40,
    height: 40,
    borderRadius: 20
  },

  textContainer: {
    flex: 1,
    width: '80%',
    gap: 5
  },

  blackText: {
    color: 'black',
    flex: 1,
    fontWeight: '700'
  },

  editedLabel: {
    flex: 1,
    fontSize: 12,
    position: 'absolute',
    top: 0,
    right: 0
  },

  date: {
    fontSize: 12,
    alignSelf: 'flex-end'
  },

  replyButton: {
    width: 125,
    fontSize: 12,
    position: 'absolute',
    top: 0,
    right: 50
  },

  directMessageLabel: {
    fontSize: 14,
    color: 'white',
    padding: 5
  },

  grayColor: {
    color: '#808080'
  },

  flexOne: {
    flex: 1
  },

  chatMessageContainer: {
    marginVertical: 10
  },

  replyContainer: {
    borderLeftWidth: 4,
    backgroundColor: '#f5f5f5',
    borderColor: '#bbb',
    paddingLeft: 8,
    borderRadius: 3,
    gap: 2,
    paddingVertical: 5,
    justifyContent: 'center'
  },

  replyTitle: {
    color: '#444'
  },

  replySubtitle: {
    fontSize: 12,
    color: '#444'
  }
});

export default ChatMessage;
